import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormGroup,
  FormGroupDirective,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { OutreachTemplatesModule } from '@techspert-io/outreachTemplates';
import moment from 'moment';
import { EMPTY, Observable, map, startWith, tap } from 'rxjs';
import { MaterialModule } from '../../../../shared/patterns/material-module';
import { CurrencyService } from '../../../../shared/services/currency/currency.service';
import { OutreachTemplateSelectComponent } from '../../../outreach-templates/components/forms/outreach-template-select/outreach-template-select.component';
import { OutreachEmailContent } from '../../models/outreach-email.forms';
import {
  CampaignEmailTemplateTypeMap,
  IOutreachEmailTemplate,
} from '../../models/outreach-email.models';

type FormGroupOutreachEmail = FormGroup<OutreachEmailContent>;
type SendConfig = OutreachEmailContent['sendConfig'];
type TemplateContent = OutreachEmailContent['templateContent'];

@Component({
  selector: 'app-outreach-email-content-form',
  templateUrl: './outreach-email-content-form.component.html',
  styleUrls: ['./outreach-email-content-form.component.scss'],
  imports: [
    ReactiveFormsModule,
    MaterialModule,
    AsyncPipe,
    OutreachTemplatesModule,
    OutreachTemplateSelectComponent,
  ],
})
export class OutreachEmailContentFormComponent implements OnInit {
  @Input() templates: IOutreachEmailTemplate[] = [];

  private readonly rootFormGroup = inject(FormGroupDirective);
  private readonly currencyService = inject(CurrencyService);
  private readonly destroyRef = inject(DestroyRef);

  templatesFiltered$: Observable<IOutreachEmailTemplate[]> = EMPTY;
  weekendWarning: boolean = false;
  currencies = this.currencyService.currencyCodes;

  get minStartDate(): string {
    return moment().format('YYYY-MM-DD');
  }

  get outreachEmailForm(): FormGroupOutreachEmail {
    return this.rootFormGroup.form;
  }

  get sendConfigCtrl(): SendConfig {
    return this.outreachEmailForm.controls.sendConfig;
  }

  get templateContentCtrl(): TemplateContent {
    return this.outreachEmailForm.controls.templateContent;
  }

  ngOnInit() {
    const templates = this.templates || [];
    this.sendConfigCtrl.controls.sendDate.valueChanges
      .pipe(startWith(null), takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (val) => (this.weekendWarning = [0, 6].includes(moment(val).day()))
      );

    this.templatesFiltered$ = this.setupTemplates(templates);
  }

  getError() {
    const errors = this.sendConfigCtrl.errors;
    return errors ? Object.values(errors).join(', ') : null;
  }

  isConnectTemplate() {
    return (
      this.outreachEmailForm.value.templateType ===
      CampaignEmailTemplateTypeMap.connect
    );
  }

  private setupTemplates(
    templates: IOutreachEmailTemplate[]
  ): Observable<IOutreachEmailTemplate[]> {
    const { templateId, templateType, templateContent } =
      this.outreachEmailForm.controls;

    return templateId.valueChanges.pipe(startWith('')).pipe(
      tap((templateId) => {
        const found = templates.find((t) => t.templateId === templateId);
        if (found) {
          templateType.patchValue(found.templateType);
          templateContent.patchValue({
            content: found.templateContent.content,
            subject: found.templateContent.subject,
            preheader: found.templateContent.preheader,
          });

          this.setupTemplateContentValidators(
            this.templateContentCtrl,
            found.templateType
          );
        }
      }),
      map((templateId) =>
        templates.filter(
          (t) =>
            `${t.name}`.toLowerCase().includes(`${templateId}`.toLowerCase()) ||
            t.templateId === templateId
        )
      )
    );
  }

  private setupTemplateContentValidators(
    templateContent: TemplateContent,
    templateType: CampaignEmailTemplateTypeMap
  ) {
    [
      templateContent.controls.content,
      templateContent.controls.subject,
    ].forEach((control) => {
      control.setValidators(
        templateType === CampaignEmailTemplateTypeMap.connect &&
          Validators.required
      );
      control.updateValueAndValidity();
    });
  }
}
